/**
 * İşveren Maliyeti Hesaplama Engine
 * 2026 Türkiye — SGK, İşsizlik, İş Kazası, 5 Puanlık İndirim dahil
 */

// ─── 2026 Sabitleri ───────────────────────────────────────────
export const YILI = 2026;
export const BRUT_ASGARI_UCRET = 33_030;
export const SGK_TAVAN_AYLIK = 297_270;

// İşçi Kesintileri
export const SGK_ISCI_ORAN = 0.14; // %14
export const ISSIZLIK_ISCI_ORAN = 0.01; // %1
export const DAMGA_VERGISI_ORAN = 0.00759; // %0.759

// İşveren Kesintileri
export const SGK_ISVEREN_ORAN = 0.195; // %19.5 (MYÖ %12 + GSS %7.5 – KVSK hariç base)
// Detaylı alt kalemler:
export const SGK_ISVEREN_MYO = 0.12; // Malullük, Yaşlılık, Ölüm (7566 sk. ile %11→%12)
export const SGK_ISVEREN_GSS = 0.075; // Genel Sağlık Sigortası
export const SGK_ISVEREN_KVSK = 0.0225; // Kısa Vadeli Sigorta Kolları (iş kazası meslek hastalığı) — standart %2.25
export const SGK_ISVEREN_TOPLAM = 0.2375; // %23.75 (MYÖ %12 + GSS %7.5 + KVSK %2.25 + İşsizlik %2)
export const ISSIZLIK_ISVEREN_ORAN = 0.02; // %2

// 5 Puanlık İşveren SGK İndirimi (5510/81-ı)
export const BES_PUAN_INDIRIM = 0.05; // %5

// Gelir Vergisi Dilimleri (Ücret — 2026)
const VERGI_DILIMLERI = [
  { altSinir: 0, ustSinir: 190_000, oran: 0.15, kumulatif: 0 },
  { altSinir: 190_000, ustSinir: 400_000, oran: 0.2, kumulatif: 28_500 },
  { altSinir: 400_000, ustSinir: 1_500_000, oran: 0.27, kumulatif: 70_500 },
  { altSinir: 1_500_000, ustSinir: 5_300_000, oran: 0.35, kumulatif: 367_500 },
  { altSinir: 5_300_000, ustSinir: Infinity, oran: 0.4, kumulatif: 1_697_500 },
];

// ─── Tipler ───────────────────────────────────────────────────
export interface IsverenMaliyetiGirdi {
  brutMaas: number;
  calisanSayisi: number;
  besPuanIndirimVar: boolean; // 5510/81-ı indirimi
  kvskOran?: number; // İş kazası oranı (default %2)
}

export interface AylikIsverenDetay {
  ay: number;
  ayAdi: string;
  brutMaas: number;
  // İşçi kesintileri
  sgkIsci: number;
  issizlikIsci: number;
  gelirVergisi: number;
  damgaVergisi: number;
  netMaas: number;
  // İşveren kesintileri
  sgkIsverenMyo: number;
  sgkIsverenGss: number;
  sgkIsverenKvsk: number;
  issizlikIsveren: number;
  besPuanIndirim: number;
  toplamIsverenEkMaliyet: number;
  toplamIsverenMaliyet: number;
}

export interface IsverenMaliyetiSonuc {
  // Girdi
  brutMaas: number;
  calisanSayisi: number;

  // Tek çalışan — aylık detaylar
  aylikDetaylar: AylikIsverenDetay[];

  // Tek çalışan — yıllık toplamlar
  yillikBrutUcret: number;
  yillikNetUcret: number;
  yillikSgkIsci: number;
  yillikIssizlikIsci: number;
  yillikGelirVergisi: number;
  yillikDamgaVergisi: number;
  yillikSgkIsveren: number;
  yillikIssizlikIsveren: number;
  yillikBesPuanIndirim: number;
  yillikIsverenEkMaliyet: number;
  yillikToplamIsverenMaliyet: number;

  // Toplam (çalışan sayısı ile çarpılmış)
  toplamYillikIsverenMaliyet: number;
  toplamAylikIsverenMaliyet: number;

  // Oranlar
  isverenMaliyetOrani: number; // brüt üzerinden ek maliyet yüzdesi
  netMaasMaliyetOrani: number; // net maaşın toplam maliyete oranı
}

// ─── Yardımcı ─────────────────────────────────────────────────
const AY_ADLARI = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

export function formatTL(value: number): string {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatOran(value: number): string {
  return `%${(value * 100).toFixed(2)}`;
}

function hesaplaKumulatifVergi(kumulatifMatrah: number): number {
  if (kumulatifMatrah <= 0) return 0;
  for (const dilim of VERGI_DILIMLERI) {
    if (kumulatifMatrah <= dilim.ustSinir) {
      return dilim.kumulatif + (kumulatifMatrah - dilim.altSinir) * dilim.oran;
    }
  }
  const son = VERGI_DILIMLERI[VERGI_DILIMLERI.length - 1];
  return son.kumulatif + (kumulatifMatrah - son.altSinir) * son.oran;
}

// ─── Ana Hesaplama ────────────────────────────────────────────
export function hesaplaIsverenMaliyeti(
  girdi: IsverenMaliyetiGirdi
): IsverenMaliyetiSonuc {
  const brutMaas = Math.max(0, girdi.brutMaas || 0);
  const calisanSayisi = Math.max(1, girdi.calisanSayisi || 1);
  const kvskOran = girdi.kvskOran ?? SGK_ISVEREN_KVSK;
  const besPuanVar = girdi.besPuanIndirimVar;

  const aylikDetaylar: AylikIsverenDetay[] = [];
  let kumulatifMatrah = 0;
  let kumulatifVergi = 0;

  // Yıllık toplamlar
  let yillikSgkIsci = 0;
  let yillikIssizlikIsci = 0;
  let yillikGelirVergisi = 0;
  let yillikDamgaVergisi = 0;
  let yillikNetUcret = 0;
  let yillikSgkIsveren = 0;
  let yillikIssizlikIsveren = 0;
  let yillikBesPuan = 0;
  let yillikIsverenEk = 0;
  let yillikIsverenToplam = 0;

  for (let ay = 0; ay < 12; ay++) {
    const sgkMatrahi = Math.min(brutMaas, SGK_TAVAN_AYLIK);

    // İşçi kesintileri
    const sgkIsci = sgkMatrahi * SGK_ISCI_ORAN;
    const issizlikIsci = sgkMatrahi * ISSIZLIK_ISCI_ORAN;
    const aylikMatrah = brutMaas - sgkIsci - issizlikIsci;
    const yeniKumulatifMatrah = kumulatifMatrah + aylikMatrah;
    const yeniKumulatifVergi = hesaplaKumulatifVergi(yeniKumulatifMatrah);
    const gelirVergisi = yeniKumulatifVergi - kumulatifVergi;
    const damgaVergisi = brutMaas * DAMGA_VERGISI_ORAN;
    const netMaas =
      brutMaas - sgkIsci - issizlikIsci - gelirVergisi - damgaVergisi;

    // İşveren kesintileri
    const sgkIsverenMyo = sgkMatrahi * SGK_ISVEREN_MYO;
    const sgkIsverenGss = sgkMatrahi * SGK_ISVEREN_GSS;
    const sgkIsverenKvsk = sgkMatrahi * kvskOran;
    const issizlikIsveren = sgkMatrahi * ISSIZLIK_ISVEREN_ORAN;
    const besPuanIndirim = besPuanVar ? sgkMatrahi * BES_PUAN_INDIRIM : 0;

    const toplamIsverenEk =
      sgkIsverenMyo +
      sgkIsverenGss +
      sgkIsverenKvsk +
      issizlikIsveren -
      besPuanIndirim;
    const toplamIsverenMaliyet = brutMaas + toplamIsverenEk;

    aylikDetaylar.push({
      ay: ay + 1,
      ayAdi: AY_ADLARI[ay],
      brutMaas,
      sgkIsci,
      issizlikIsci,
      gelirVergisi,
      damgaVergisi,
      netMaas,
      sgkIsverenMyo,
      sgkIsverenGss,
      sgkIsverenKvsk,
      issizlikIsveren,
      besPuanIndirim,
      toplamIsverenEkMaliyet: toplamIsverenEk,
      toplamIsverenMaliyet,
    });

    kumulatifMatrah = yeniKumulatifMatrah;
    kumulatifVergi = yeniKumulatifVergi;
    yillikSgkIsci += sgkIsci;
    yillikIssizlikIsci += issizlikIsci;
    yillikGelirVergisi += gelirVergisi;
    yillikDamgaVergisi += damgaVergisi;
    yillikNetUcret += netMaas;
    yillikSgkIsveren += sgkIsverenMyo + sgkIsverenGss + sgkIsverenKvsk;
    yillikIssizlikIsveren += issizlikIsveren;
    yillikBesPuan += besPuanIndirim;
    yillikIsverenEk += toplamIsverenEk;
    yillikIsverenToplam += toplamIsverenMaliyet;
  }

  const yillikBrut = brutMaas * 12;

  return {
    brutMaas,
    calisanSayisi,
    aylikDetaylar,
    yillikBrutUcret: yillikBrut,
    yillikNetUcret,
    yillikSgkIsci,
    yillikIssizlikIsci,
    yillikGelirVergisi,
    yillikDamgaVergisi,
    yillikSgkIsveren,
    yillikIssizlikIsveren,
    yillikBesPuanIndirim: yillikBesPuan,
    yillikIsverenEkMaliyet: yillikIsverenEk,
    yillikToplamIsverenMaliyet: yillikIsverenToplam,
    toplamYillikIsverenMaliyet: yillikIsverenToplam * calisanSayisi,
    toplamAylikIsverenMaliyet: (yillikIsverenToplam / 12) * calisanSayisi,
    isverenMaliyetOrani: yillikBrut > 0 ? yillikIsverenEk / yillikBrut : 0,
    netMaasMaliyetOrani:
      yillikIsverenToplam > 0 ? yillikNetUcret / yillikIsverenToplam : 0,
  };
}
